import classNames from "classnames";
import {
	Icon,
	ListItem
} from "client/Components";
import { ItemSelectionKind, SelectableItemOptions } from "client/Hooks";
import { DocumentItemType } from "client/Api/Documents/SharePoint/common";
import { ActionAndOthers } from "client_legacy/ndaw/actions";
import {
	ReactElement,
	RefObject,
	useCallback,
	useMemo,
	useRef,
	useState
} from "react";
import { DocumentListItemActions } from "../DocumentListItem";
import { DocumentNameKind, getNameAndThumbnail } from "../DocumentName";
import styles from "./ItemPickerListItem.module.less";
import { FileThumbnailKind } from "client_legacy/Documents/fileExtensions";

export type ItemPickerListItemProps<TActionContext> = {
	name: string;
	additionalInformation?: string | ReactElement;
	documentKind: DocumentItemType;
	isBookmarked?: boolean;
	isInfected?: boolean;
	isInSelectMode: boolean;
	selectable: SelectableItemOptions;
	onOpenItem: () => void;
	isDisabled: boolean;
	actions: readonly ActionAndOthers<TActionContext>[];
	actionContext: TActionContext;
};

export const ItemPickerListItem = <TActionContext,>(props: ItemPickerListItemProps<TActionContext>): ReactElement => {
	const {
		name,
		additionalInformation,
		documentKind,
		isBookmarked = false,
		isInfected = false,
		isInSelectMode,
		selectable,
		onOpenItem,
		isDisabled,
		actions,
		actionContext
	} = props;

	const [isActionsOpen, setIsActionsOpen] = useState<boolean>(false);
	const actionRef: RefObject<HTMLButtonElement | null> = useRef<HTMLButtonElement>(null);

	const thumbnail = useMemo(
		(): FileThumbnailKind => {
			return getNameAndThumbnail(
				name,
				documentKind,
				{
					kind: DocumentNameKind.Default,
					hasBookmark: isBookmarked,
					isInfected
				}
			).thumbnail;
		},
		[
			documentKind,
			isBookmarked,
			isInfected,
			name
		]
	);

	const openItemHandler = useCallback(
		(): void => {
			onOpenItem();

			if (!selectable) {
				return;
			}
			if (isInSelectMode) {
				selectable.onSelect(ItemSelectionKind.Append);
				return;
			}

			selectable.onSelect(ItemSelectionKind.Override);
		},
		[
			isInSelectMode,
			onOpenItem,
			selectable
		]
	);

	return (
		<div className={classNames(
			{
				[styles.root_isDisabled]: isDisabled
			}
		)}>
			<ListItem
				isActive={selectable ? selectable.isSelected : false}
			>
				{isInSelectMode && (
					selectable ? (
						<ListItem.Checkbox
							value={selectable.isSelected}
							onChange={() => selectable.onSelect(ItemSelectionKind.Append)}
						/>
					) : (
						<ListItem.CheckboxPlaceholder />
					)
				)}

				<ListItem.Content
					onClick={openItemHandler}
					thumbnail={(
						<Icon
							value={{
								kind: "svg",
								name: thumbnail
							}}
							size="large"
						/>
					)}
				>
					<ListItem.Row>
						<ListItem.Title>
							{name}
						</ListItem.Title>

						{isInfected && (
							<ListItem.TitleIcon kind="shield-xmark" color="danger" />
						)}
						{isBookmarked && (
							<ListItem.TitleIcon kind="star-filled" color="bookmark" />
						)}
					</ListItem.Row>

					{additionalInformation && (
						<ListItem.SubTitle>
							{additionalInformation}
						</ListItem.SubTitle>
					)}
				</ListItem.Content>

				{selectable && (
					<>
						<ListItem.Action
							icon="ellipsis"
							ref={actionRef}
							onClick={() => setIsActionsOpen(true)}
							trackingId="actions"
						/>
						{actionRef.current && isActionsOpen && (
							<DocumentListItemActions
								actions={actions}
								context={actionContext}
								target={actionRef.current}
								onClose={() => setIsActionsOpen(false)}
								name={name}
							/>
					)}
					</>
				)}
			</ListItem>
		</div>
	);
};
ItemPickerListItem.displayName = "ItemPickerListItem";
